import React from 'react';
import styled from 'styled-components';

const MethodologyContainer = styled.section`
  id: methodology;
  padding: ${({ theme }) => `${theme.spacing.content.section} ${theme.spacing.content.base}`};
  background-image: url('/images/solutions-overview.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;

  /* Light semi-transparent overlay for text readability */
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.75);
    z-index: 0;
  }

  /* All child elements need to be above the overlay */
  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => `${theme.spacing['3xl']} ${theme.spacing.content.narrow}`};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => `${theme.spacing['2xl']} ${theme.spacing.content.narrow}`};
  }
`;

const MethodologyContent = styled.div`
  max-width: ${({ theme }) => theme.breakpoints.wide};
  margin: 0 auto;
  text-align: center;
`;

const MethodologyTitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize['4xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  }
`;

const MethodologySubtitle = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.light};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 ${({ theme }) => theme.spacing['3xl']} 0;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`;

const MethodologyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: ${({ theme }) => theme.spacing['2xl']};
  margin-bottom: ${({ theme }) => theme.spacing['3xl']};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.xl};
  }
`;

const MethodologyCard = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  padding: ${({ theme }) => theme.spacing['2xl']};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.card};
  transition: all ${({ theme }) => theme.transitions.base};
  text-align: left;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${({ theme, $variant }) => 
      $variant === 'intelligence' ? theme.colors.primary.main :
      $variant === 'collaboration' ? theme.colors.status.success :
      $variant === 'transformation' ? theme.colors.secondary.main :
      theme.colors.primary.main};
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: ${({ theme }) => theme.shadows.cardHover};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.xl};
  }
`;

const CardIcon = styled.div`
  width: 70px;
  height: 70px;
  background: ${({ theme, $variant }) => 
    $variant === 'intelligence' ? theme.colors.primary.main :
    $variant === 'collaboration' ? 'transparent' :
    $variant === 'transformation' ? theme.colors.secondary.main :
    theme.colors.primary.main};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  color: ${({ theme, $variant }) => 
    $variant === 'collaboration' ? theme.colors.status.success : theme.colors.text.inverse};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 60px;
    height: 60px;
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
  }
`;

const CardTitle = styled.h4`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.base} 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
  }
`;

const CardDescription = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const CardFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const Feature = styled.li`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.muted};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  padding-left: ${({ theme }) => theme.spacing.lg};
  position: relative;
  line-height: ${({ theme }) => theme.typography.lineHeight.base};

  &:before {
    content: "→";
    position: absolute;
    left: 0;
    color: ${({ theme, $variant }) => 
      $variant === 'intelligence' ? theme.colors.primary.main :
      $variant === 'collaboration' ? theme.colors.status.success :
      $variant === 'transformation' ? theme.colors.secondary.main :
      theme.colors.primary.main};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const ProcessFlow = styled.div`
  background: ${({ theme }) => `linear-gradient(135deg, ${theme.colors.primary.main} 0%, ${theme.colors.primary.light} 100%)`};
  color: ${({ theme }) => theme.colors.text.inverse};
  padding: ${({ theme }) => theme.spacing['3xl']};
  border-radius: ${({ theme }) => theme.borderRadius['2xl']};
  margin-bottom: ${({ theme }) => theme.spacing['3xl']};
  box-shadow: ${({ theme }) => theme.shadows.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing['2xl']};
  }
`;

const ProcessTitle = styled.h4`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  margin: 0 0 ${({ theme }) => theme.spacing.xl} 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.inverse};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
  }
`;

const ProcessSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${({ theme }) => theme.spacing.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.lg};
  }
`;

const ProcessStep = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  text-align: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

const StepNumber = styled.div`
  width: 40px;
  height: 40px;
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${({ theme }) => theme.spacing.base} auto;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 35px;
    height: 35px;
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`;

const StepTitle = styled.h5`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  color: ${({ theme }) => theme.colors.text.inverse};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`;

const StepDescription = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  line-height: ${({ theme }) => theme.typography.lineHeight.base};
  margin: 0;
  color: ${({ theme }) => theme.colors.text.inverse};
  opacity: 0.9;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const InnovationBanner = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  padding: ${({ theme }) => theme.spacing['2xl']};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  text-align: center;
  box-shadow: ${({ theme }) => theme.shadows.card};
  border: 2px solid ${({ theme }) => theme.colors.status.success};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.xl};
  }
`;

const BannerTitle = styled.h4`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.status.success};
  margin: 0 0 ${({ theme }) => theme.spacing.base} 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
  }
`;

const BannerText = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  margin: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const MethodologySection = () => {
  console.log('MethodologySection updated - contrast and colors fixed');
  return (
    <MethodologyContainer id="methodology">
      <MethodologyContent>
        <MethodologyTitle>AI-Assisted Strategic Intelligence for Industry 5.0 Leadership</MethodologyTitle>
        <MethodologySubtitle>
          Combining human strategic intelligence with AI-assisted analysis to position clients 
          advantageously for next-generation manufacturing transformation.
        </MethodologySubtitle>

        <MethodologyGrid>
          <MethodologyCard $variant="intelligence">

            <CardTitle>Competitive Intelligence Framework</CardTitle>
            <CardDescription>
              Systematic competitive landscape analysis combining human strategic insight with 
              AI-assisted pattern recognition for comprehensive industry transformation understanding.
            </CardDescription>
            <CardFeatures>
              <Feature $variant="intelligence">Continuous industry transformation monitoring</Feature>
              <Feature $variant="intelligence">Strategic positioning for emerging technologies</Feature>
              <Feature $variant="intelligence">Competitive advantage identification</Feature>
              <Feature $variant="intelligence">Long-term strategic planning integration</Feature>
              <Feature $variant="intelligence">Global transformation forces analysis</Feature>
            </CardFeatures>
          </MethodologyCard>

          <MethodologyCard $variant="collaboration">

            <CardTitle>Human-AI Collaboration Pioneer</CardTitle>
            <CardDescription>
              Demonstrating Industry 5.0 methodology through proven implementation of human strategic 
              intelligence combined with AI-assisted analysis for next-generation competitive advantages.
            </CardDescription>
            <CardFeatures>
              <Feature $variant="collaboration">Industry 5.0 human-AI collaboration modeling</Feature>
              <Feature $variant="collaboration">AI-assisted strategic decision support</Feature>
              <Feature $variant="collaboration">Early adoption competitive advantage</Feature>
              <Feature $variant="collaboration">Next-generation workflow demonstration</Feature>
              <Feature $variant="collaboration">Strategic intelligence amplification</Feature>
            </CardFeatures>
          </MethodologyCard>

          <MethodologyCard $variant="transformation">

            <CardTitle>Crisis-to-Competitive-Advantage Transformation</CardTitle>
            <CardDescription>
              Systematic framework for transforming operational crises into strategic competitive 
              advantages through comprehensive technology leadership and long-term positioning.
            </CardDescription>
            <CardFeatures>
              <Feature $variant="transformation">Immediate crisis resolution protocols</Feature>
              <Feature $variant="transformation">Root cause analysis and systematic evaluation</Feature>
              <Feature $variant="transformation">Strategic positioning development</Feature>
              <Feature $variant="transformation">Long-term transformation planning</Feature>
              <Feature $variant="transformation">Competitive advantage creation from learnings</Feature>
            </CardFeatures>
          </MethodologyCard>
        </MethodologyGrid>

        <ProcessFlow>
          <ProcessTitle>Strategic Response Framework</ProcessTitle>
          <ProcessSteps>
            <ProcessStep>
              <StepNumber>1</StepNumber>
              <StepTitle>Crisis Resolution</StepTitle>
              <StepDescription>
                Immediate technical and operational problem-solving with comprehensive 
                stakeholder communication and stabilization protocols.
              </StepDescription>
            </ProcessStep>

            <ProcessStep>
              <StepNumber>2</StepNumber>
              <StepTitle>Root Cause Analysis</StepTitle>
              <StepDescription>
                Systematic infrastructure and process evaluation using AI-assisted analysis 
                to identify underlying patterns and systemic vulnerabilities.
              </StepDescription>
            </ProcessStep>

            <ProcessStep>
              <StepNumber>3</StepNumber>
              <StepTitle>Strategic Positioning</StepTitle>
              <StepDescription>
                Competitive advantage development from crisis learnings, positioning 
                the organization advantageously for next-decade industry transitions.
              </StepDescription>
            </ProcessStep>

            <ProcessStep>
              <StepNumber>4</StepNumber>
              <StepTitle>Transformation Leadership</StepTitle>
              <StepDescription>
                5-10 year strategic technology leadership ensuring sustained competitive 
                advantage through continuous adaptation and innovation.
              </StepDescription>
            </ProcessStep>
          </ProcessSteps>
        </ProcessFlow>

        <InnovationBanner>
          <BannerTitle>Industry 5.0 Methodology Pioneer</BannerTitle>
          <BannerText>
            Proven implementation of human strategic intelligence combined with AI-assisted analysis, 
            providing clients with competitive advantages through early adoption of next-generation 
            collaborative approaches that position them as leaders in manufacturing transformation.
          </BannerText>
        </InnovationBanner>
      </MethodologyContent>
    </MethodologyContainer>
  );
};

export default MethodologySection;